import { createSelector, createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';
import { formatISO } from 'date-fns';
import { v4 } from 'uuid';

import { mifidApi } from '@/api/mifid/index.ts';
import { getWorkspaceMatchFulfilled } from '@/api/workspace/index.ts';
import { mapToTargetAccumulator } from '@/domain/FxTargetAccumulator/FxTargetAccumulator.utils.ts';
import type { AccumulatorReplyMessage } from '@/store/forwardAccumulator/api/multiPass/requestForwardAccumulator.model.ts';
import { reduceTargetAccumulatorOnFetchSharedWorkspace } from '@/store/sharedWorkspace/workflows/getSharedTab.workflow.ts';
import { reduceTargetAccumulatorStateOnSharedTabRemoved } from '@/store/sharedWorkspace/workflows/onSharedTabRemoved.workflow.ts';
import { targetAccumulatorApi } from '@/store/targetAccumulator/api/index.ts';
import { createTargetAccumulatorQuote } from '@/store/targetAccumulator/api/multiPass/createTargetAccumulatorQuote';
import { getTargetAccumulatorRfsId } from '@/store/targetAccumulator/selectors.ts';
import type {
  DisplayMode,
  FxTargetAccumulator,
  TargetAccumulatorById,
  TargetAccumulatorRfs,
} from '@/store/targetAccumulator/state.ts';
import { initTargetAccumulator } from '@/store/targetAccumulator/utils.ts';
import { addTile, duplicateTile, removeTile, updateTileProductType } from '@/store/tiles/slice.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import {
  type IHeartbeat,
  type IQuoteAbort,
  type IQuoteTraderNotification,
  isQuoteWithdraw,
  type QuoteWithPrice,
} from '@/utils/rfs/type.ts';

const createTargetAccumulatorSelector = createSelector.withTypes<TargetAccumulatorById>();

export const targetAccumulatorSlice = createSlice({
  name: 'targetAccumulator',
  reducerPath: 'targetAccumulator',
  initialState: {} as TargetAccumulatorById,
  reducers: {
    targetAccumulatorClientIdUpdated: (
      state,
      action: PayloadAction<
        | { tileId: string; clientId: number | undefined; isTabClientOverriden: true }
        | { tileId: string; clientId: number; isTabClientOverriden: false }
      >,
    ) => {
      const tile = state[action.payload.tileId];

      if (isNotDefined(tile) || tile.clientId === action.payload.clientId) {
        return;
      }

      if (action.payload.isTabClientOverriden === true) {
        tile.clientId = action.payload.clientId;
      }

      tile.properties.tiering = undefined;
    },
    setTargetAccumulatorMaxDate(state, action: PayloadAction<{ tileId: string; maxDate: string | undefined }>) {
      const { tileId, maxDate } = action.payload;
      withTargetAccumulator(tileId, state, (targetAccumulator) => {
        targetAccumulator.properties.maxDate = maxDate;
      });
    },
    notionalCurrencyChanged: (
      state,
      {
        payload: { tileId, notionalCurrencyIndex },
      }: PayloadAction<{ tileId: string; notionalCurrencyIndex: CurrencyIndex }>,
    ) => {
      if (isNotDefined(state[tileId]?.properties.notionalCurrencyIndex)) {
        return state;
      }

      state[tileId].properties.notionalCurrencyIndex = notionalCurrencyIndex;
    },
    targetAccumulatorCrossCurrencyUpdated(state, action: PayloadAction<{ tileId: string; crossCurrency: string }>) {
      const tile = state[action.payload.tileId];
      if (isNotDefined(tile)) {
        return;
      }
      tile.properties.crossCurrency = action.payload.crossCurrency;
    },
    targetAccumulatorReset: (state, action: PayloadAction<{ tileId: string }>) => {
      const { tileId } = action.payload;

      withTargetAccumulator(tileId, state, (targetAccumulator) => {
        state[tileId] = initTargetAccumulator(targetAccumulator.clientId, targetAccumulator.properties.currencyPair);
      });
    },
    targetAccumulatorTieringUpdated(state, action: PayloadAction<{ tileId: string; tiering: string }>) {
      const tile = state[action.payload.tileId];
      if (isNotDefined(tile)) {
        return;
      }

      tile.properties.tiering = action.payload.tiering;
    },
    changeClient(state, action: PayloadAction<{ tileId: string; clientId: number | undefined }>) {
      const { tileId, clientId } = action.payload;
      withTargetAccumulator(tileId, state, (tile) => {
        tile.clientId = clientId;
      });
    },
    targetAccumulatorErrorOccurred: (state, action: PayloadAction<{ tileId: string }>) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile)) {
        return;
      }

      tile.tradeCapture = {
        error: 'TIMEOUT',
        status: 'ERROR',
        sessionId: tile.tradeCapture.sessionId,
        version: tile.tradeCapture.version,
      };
    },
    targetAccumulatorRfsHeartbeat(_state, _action: PayloadAction<{ tileId: string; heartbeat: IHeartbeat }>) {},
    targetAccumulatorRfsQuoteAborted(state, action: PayloadAction<{ tileId: string; reply: IQuoteAbort }>) {
      const { tileId, reply } = action.payload;
      withTargetAccumulator(tileId, state, (accumulator) => {
        if (reply.QuoteReject.ErrorCode === 'EM_TIMEOUT') {
          accumulator.properties.rfs = makeExpired(accumulator.properties.rfs);
        } else {
          accumulator.properties.rfs = {
            status: 'ERROR',
            rfsId: getTargetAccumulatorRfsId(accumulator),
            error: {
              multipassErrorCode: reply.QuoteReject.MultipassErrorCode,
              errorCode: reply.QuoteReject.ErrorCode,
              errorMessage: reply.QuoteReject.ErrorMessage,
            },
          };
        }
      });
    },
    targetAccumulatorRfsQuoteReceived(
      state,
      action: PayloadAction<{ tileId: string; message: QuoteWithPrice<AccumulatorReplyMessage> }>,
    ) {
      const { tileId, message } = action.payload;

      withTargetAccumulator(tileId, state, (accumulator) => {
        const quote = createTargetAccumulatorQuote(message);
        if (accumulator.properties.rfs?.status === 'PRICING' || accumulator.properties.rfs?.status === 'AWAITING') {
          const initialRfsWindow =
            accumulator.properties.rfs.status === 'PRICING'
              ? accumulator.properties.rfs.initialRfsWindow
              : quote.rfsWindow;
          accumulator.properties.rfs = {
            status: 'PRICING',
            quote,
            rfsId: accumulator.properties.rfs.rfsId,
            quoteType: isQuoteWithdraw(message) ? 'Indicative' : quote.quoteType,
            quoteId: quote.quoteId,
            traderId: accumulator.properties.rfs.traderId,
            initialRfsWindow,
          };
        }
      });
    },
    targetAccumulatorTraderNotificationReceived: (
      state,
      action: PayloadAction<{ tileId: string; message: IQuoteTraderNotification<AccumulatorReplyMessage> }>,
    ) => {
      withTargetAccumulator(action.payload.tileId, state, (accumulator) => {
        const traderId = action.payload.message.AccumulatorQuoteReply.TraderId ?? undefined;

        if (accumulator.properties.rfs.status === 'PRICING' || accumulator.properties.rfs.status === 'AWAITING') {
          accumulator.properties.rfs = {
            ...accumulator.properties.rfs,
            traderId,
          };
        }
      });
    },

    targetAccumulatorPending: (state, action: PayloadAction<{ tileId: string; executionId: string }>) => {
      const { tileId, executionId } = action.payload;

      withTargetAccumulator(tileId, state, (targetAccumulator) => {
        targetAccumulator.execution = {
          status: 'PENDING',
          executionId,
        };

        if (isDefined(targetAccumulator.properties.rfs)) {
          targetAccumulator.properties.rfs = makeExpired(targetAccumulator.properties.rfs);
        }
      });
    },

    targetAccumulatorExecutionSucceed: (state, action: PayloadAction<{ tileId: string }>) => {
      withTargetAccumulator(action.payload.tileId, state, (targetAccumulator) => {
        if (isNotDefined(targetAccumulator.execution)) {
          return;
        }

        targetAccumulator.execution.status = 'SUCCESS';
      });
    },

    targetAccumulatorExecutionRejected: (state, action: PayloadAction<{ tileId: string }>) => {
      withTargetAccumulator(action.payload.tileId, state, (targetAccumulator) => {
        if (isNotDefined(targetAccumulator.execution)) {
          return;
        }

        targetAccumulator.execution.status = 'REJECTED';
      });
    },

    targetAccumulatorRejectedExecutionReset: (state, action: PayloadAction<{ tileId: string }>) => {
      withTargetAccumulator(action.payload.tileId, state, (targetAccumulator) => {
        if (isNotDefined(targetAccumulator.execution)) {
          return;
        }

        targetAccumulator.execution = undefined;
      });
    },

    targetAccumulatorExecutionReset: (state, action: PayloadAction<{ tileId: string }>) => {
      const { tileId } = action.payload;

      withTargetAccumulator(tileId, state, (targetAccumulator) => {
        targetAccumulator.execution = undefined;
      });
    },

    targetAccumulatorSetDisplayMode: (state, action: PayloadAction<{ tileId: string; displayMode: DisplayMode }>) => {
      const { tileId, displayMode } = action.payload;

      withTargetAccumulator(tileId, state, (accumulator) => {
        accumulator.properties.uiState.displayMode = displayMode;
      });
    },
  },

  extraReducers: (builder) => {
    reduceTargetAccumulatorStateOnSharedTabRemoved(builder);
    builder.addCase(updateTileProductType, (state, { payload: { tileId, productType, currencyPair } }) => {
      if (isDefined(state[tileId]) && productType !== 'TargetAccumulator') {
        delete state[tileId];
      }

      if (productType === 'TargetAccumulator') {
        state[tileId] = initTargetAccumulator(undefined, currencyPair);
      }
    });

    builder.addCase(addTile, (state, { payload }) => {
      const {
        tile: { tileId, productType },
      } = payload;
      if (isDefined(state?.[tileId])) {
        return state;
      }

      if (productType === 'TargetAccumulator') {
        state[tileId] = initTargetAccumulator();
      }
    });
    builder.addCase(removeTile, (state, { payload: tileId }) => {
      if (isNotDefined(state[tileId])) {
        return state;
      }

      delete state[tileId];
    });

    builder.addCase(duplicateTile, (state, action) => {
      const { sourceTileId, newTileId } = action.payload;

      const sourceTile = state[sourceTileId];

      if (isNotDefined(sourceTile)) {
        return state;
      }

      if (isDefined(state[newTileId])) {
        return state;
      }

      return {
        ...state,
        [newTileId]: {
          ...sourceTile,
          properties: {
            ...sourceTile.properties,
            rfs: { status: 'CANCELLED' },
          },
          tradeCapture: {
            status: 'INITIALIZED',
            sessionId: v4(),
            version: -1,
          },
        },
      };
    });
    builder.addMatcher(
      targetAccumulatorApi.endpoints.checkTargetAccumulator.matchPending,
      function onTradeCaptureUpdated(state, action) {
        const { tileId } = action.meta.arg.originalArgs;
        const currentTile = state[tileId];
        if (isNotDefined(currentTile)) {
          return;
        }

        currentTile.tradeCapture.status = 'CHECKING';
      },
    );

    builder.addMatcher(
      targetAccumulatorApi.endpoints.checkTargetAccumulator.matchRejected,
      function onTradeCaptureError(state, action) {
        const { tileId } = action.meta.arg.originalArgs;

        const currentTile = state[tileId];
        if (isNotDefined(currentTile)) {
          return;
        }
        currentTile.tradeCapture = {
          status: 'ERROR',
          // TODO: this can be a TIMEOUT, but this can also be another type of error
          error: 'TIMEOUT',
          // TODO: are those fields relevant?
          sessionId: currentTile.tradeCapture.sessionId,
          version: currentTile.tradeCapture.version,
        };
      },
    );

    builder.addMatcher(
      targetAccumulatorApi.endpoints.checkTargetAccumulator.matchFulfilled,
      function onTradeCaptureUpdated(state, action) {
        const { tileId } = action.meta.arg.originalArgs;

        const currentTile = state[tileId];

        if (isNotDefined(currentTile) || isDefined(currentTile.execution)) {
          // already executed
          return;
        }

        const tcResponse = action.payload;
        currentTile.tradeCapture = tcResponse.tradeCapture;

        if (tcResponse.type === 'error') {
          return;
        }

        // clear tiering to let esp listeners update it
        // TODO: not very obvious
        if (currentTile.properties.currencyPair !== tcResponse.properties.currencyPair) {
          currentTile.properties.tiering = undefined;
          currentTile.properties.isEspDefaultTiering = undefined;
        }

        currentTile.properties = {
          ...currentTile.properties,
          ...tcResponse.properties,
        };

        // TODO FSA remove isPriceable if really not needed
        currentTile.properties.isPriceable = tcResponse.tradeCapture.isReadyToPrice;

        if (isDefined(tcResponse.properties.schedule)) {
          currentTile.properties.scheduleIds = Object.keys(tcResponse.properties.schedule);
        }
      },
    );
    // REQUEST
    builder.addMatcher(
      targetAccumulatorApi.endpoints.requestTargetAccumulatorRfs.matchPending,
      function beforeRfsRequest(state, action) {
        const tileId = action.meta.arg.originalArgs.tileId;
        withTargetAccumulator(tileId, state, (targetAccumulator) => {
          const rfsId = action.meta.arg.originalArgs.rfsId;
          targetAccumulator.properties.rfs = {
            status: 'AWAITING',
            rfsId,
            quoteType: action.meta.arg.originalArgs.body.forceTradable ? 'Tradeable' : 'Indicative',
          };
          targetAccumulator.properties.mifid = { records: [] };
        });
      },
    );

    // TIMESTAMP
    builder.addMatcher(mifidApi.endpoints.recordPrice.matchFulfilled, (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      withTargetAccumulator(tileId, state, (targetAccumulator) => {
        if (isNotDefined(targetAccumulator.properties.mifid)) {
          return;
        }

        targetAccumulator.properties.mifid.records.push({ date: formatISO(action.meta.arg.originalArgs.date) });
      });
    });
    builder.addMatcher(mifidApi.endpoints.recordPrice.matchRejected, (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      withTargetAccumulator(tileId, state, (targetAccumulator) => {
        if (isNotDefined(targetAccumulator.properties.mifid)) {
          return;
        }

        targetAccumulator.properties.mifid.records.push({
          date: formatISO(action.meta.arg.originalArgs.date),
          isError: true,
        });
      });
    });

    // REQUEST CANCELLED
    builder.addMatcher(
      targetAccumulatorApi.endpoints.requestTargetAccumulatorRfs.matchRejected,
      function beforeRfsRequest(state, action) {
        const tileId = action.meta.arg.originalArgs.tileId;
        withTargetAccumulator(tileId, state, (targetAccumulator) => {
          const rfsId = action.meta.arg.originalArgs.rfsId;
          targetAccumulator.properties.rfs = {
            status: 'ERROR',
            rfsId,
            error: {
              multipassErrorCode: '',
              errorCode: 'REQUEST_ERROR',
              errorMessage: 'Request to pricing failed',
            },
          };
        });
      },
    );

    // CANCEL
    builder.addMatcher(
      targetAccumulatorApi.endpoints.cancelTargetAccumulatorRfs.matchPending,
      function beforeCancelRfs(state, action) {
        const { tileId, removeQuote } = action.meta.arg.originalArgs;
        withTargetAccumulator(tileId, state, (targetAccumulator) => {
          if (removeQuote) {
            targetAccumulator.properties.rfs = {
              status: 'CANCELLED',
            };
          } else {
            targetAccumulator.properties.rfs = makeExpired(targetAccumulator.properties.rfs);
          }
        });
      },
    );

    builder.addMatcher(getWorkspaceMatchFulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }

      // clear all products created before workspace has loaded
      for (const id of Object.keys(state)) {
        delete state[id];
      }

      action.payload.products.targetAccumulator?.forEach((targetAccumulatorProduct) => {
        state[targetAccumulatorProduct.tileId] = mapToTargetAccumulator(targetAccumulatorProduct);
      });
    });
    reduceTargetAccumulatorOnFetchSharedWorkspace(builder);
  },
  selectors: {
    getTargetAccumulator: (state, tileId: string): FxTargetAccumulator | undefined => state[tileId],
    getAllTargetAccumulatorProducts: createTargetAccumulatorSelector([(state) => state], (state) =>
      Object.entries(state).map(([tileId, targetAccumulator]) => ({ tileId, ...targetAccumulator })),
    ),
  },
});

export const {
  targetAccumulatorExecutionRejected,
  targetAccumulatorExecutionReset,
  targetAccumulatorExecutionSucceed,
  targetAccumulatorPending,
  targetAccumulatorRejectedExecutionReset,
  targetAccumulatorErrorOccurred,
} = targetAccumulatorSlice.actions;
export const { getAllTargetAccumulatorProducts } = targetAccumulatorSlice.selectors;

function withTargetAccumulator(
  tileId: string,
  state: TargetAccumulatorById,
  action: (accumulator: Draft<FxTargetAccumulator>) => void,
) {
  const currentAccumulator = state[tileId];
  if (isDefined(currentAccumulator)) {
    action(currentAccumulator);
  }
}

function makeExpired(rfs: TargetAccumulatorRfs): TargetAccumulatorRfs {
  if (rfs.status === 'EXPIRED' || rfs.status === 'PRICING') {
    return {
      status: 'EXPIRED',
      quote: rfs.quote,
    };
  }
  return { status: 'CANCELLED' };
}
